import { useState, useEffect } from "react";
import { tss } from "tss-react/mui";
import { SketchPicker } from "react-color";
import ViewerEvents from "events/ViewerEvents";

var useStyles = tss.create(({theme}) => ({
    root: {
        width: 260,
        height: 200,
        textAlign: "center",
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        verticalAlign: "middle",
        display: "inline-block",
    },
    title: {
        display: "inline-block",
        position: "relative",
        fontWeight: 600,
        left: 5,
    },
    button: {
        width: 30,
        height: 30,
        minWidth: 30,
    },
    centerDiv: {
        width: "96%",
        textAlign: "center",
    },
    content: {
        marginTop: 10,
        height: 140,
    },
    table: {
        width: "100%",
        border: 0,
    },
    cell: {
        width: "60%",
        paddingLeft: 10,
    },
    sliderInput: {
        display: "inline-block",
        width: 70,
        outline: 0,
        border: 0,
        borderBottom: `solid 1px ${theme.palette.divider}`,
        textAlign: "right",
        padding: "5px 5px",
        background: "transparent",
        color: theme.palette.text.secondary
    },
    inputColor: {
        position: "relative",
    },
    colorBox: {
        cursor: "pointer",
        width: 15,
        height: 15,
        display: "inline-block",
        backgroundColor: "#ccc",
        position: "relative",
        left: 0,
        top: 2,
        border: "solid 1px #E6E6E6",
    },
    palette: {
        width: 220,
        height: 196,
        overflowY: "hidden",
        position: "relative",
        backgroundColor: "yellow",
    },
}));

let panel: any;
let sunColorRef: any;
let ambientColorRef: any;
let globalSettings: any = {};

function toHex(v: number) {
    v = parseInt(v + "");
    var hex = Number(v).toString(16);
    if (hex.length < 2) {
        hex = "0" + hex;
    }
    return hex.toUpperCase();
}

function getCSSColor(rgb: any) {
    return "#" + toHex(255 * rgb.r) + toHex(255 * rgb.g) + toHex(255 * rgb.b);
}

var previousColor = { r: 1, g: 1, b: 1 };

function parseColor(input: string) {
    var collen = (input.length - 1) / 3;
    var fact = [17, 1, 0.062272][collen - 1];
    var rgb: any = {
        r: Math.round(parseInt(input.substr(1, collen), 16) * fact) / 255.0,
        g: Math.round(parseInt(input.substr(1 + collen, collen), 16) * fact) / 255.0,
        b: Math.round(parseInt(input.substr(1 + 2 * collen, collen), 16) * fact) / 255.0,
    };

    if (isNaN(rgb.r)) {
        return previousColor;
    }
    previousColor = rgb;
    return rgb;
}

function getSettings(viewer: any) {
    return {
        sunColor: getCSSColor(viewer ? viewer._renderSettings.sunColor : { r: 1, g: 1, b: 1 }),
        ambientColor: getCSSColor(viewer ? viewer._renderSettings.ambientColor : { r: 0.5, g: 0.5, b: 0.5 }),
        sunIntensity: viewer ? viewer._renderSettings.sunIntensity : 1.0,
        ambientIntensity: viewer ? viewer._renderSettings.ambientIntensity : 1.0,
        sunAzimuth: viewer ? viewer._renderSettings.sunAzimuth : 0.0,
        sunElevation: viewer ? viewer._renderSettings.sunElevation : 45.0,
    };
}

type IProps = {
    show: boolean;
};

export default function LightingPanel(props: IProps) {
    const { classes } = useStyles();
    const viewer = ViewerEvents.viewer;
    const [update, setUpdate] = useState(false);
    const initialState = getSettings(viewer);
    globalSettings = { ...initialState };
    const { sunColor, ambientColor, sunIntensity, ambientIntensity, sunAzimuth, sunElevation } = globalSettings;
    const [sunColorValue, setSunColor] = useState(sunColor);
    const [ambientColorValue, setAmbientColor] = useState(ambientColor);

    useEffect(() => {
        if (update) {
            setUpdate(false);
        }
    }, [update]);

    const handleColorChange = (ref: string, value: any) => {
        if (ref === "sunColor") {
            if (value.length !== 7 || value.indexOf("#") === -1) {
                setSunColor(value.toUpperCase());
                return;
            }
            globalSettings["sunColor"] = value.toUpperCase();
            updateRenderSettings();
        } else {
            if (value.length !== 7 || value.indexOf("#") === -1) {
                setAmbientColor(value.toUpperCase());
                return;
            }
            globalSettings["ambientColor"] = value.toUpperCase();
            updateRenderSettings();
        }
    };

    const closeTool = (ref: string) => {
        if (ref === "sunColor") sunColorRef.style.display = "none";
        else ambientColorRef.style.display = "none";
    };

    const showPalette = (ref: string) => {
        if (ref === "sunColor") {
            if (sunColorRef.style.display === "table-cell") {
                closeTool("sunColor");
                panel.style.height = "200px";
            } else {
                panel.style.height = "400px";
                sunColorRef.style.display = "table-cell";
                closeTool("ambientColor");
            }
        } else {
            if (ambientColorRef.style.display === "table-cell") {
                closeTool("ambientColor");
                panel.style.height = "200px";
            } else {
                panel.style.height = "400px";
                ambientColorRef.style.display = "table-cell";
                closeTool("sunColor");
            }
        }
    };

    const updateRenderSettings = () => {
        viewer._renderSettings.sunColor = parseColor(globalSettings["sunColor"]);
        viewer._renderSettings.ambientColor = parseColor(globalSettings["ambientColor"]);
        viewer._renderSettings.sunIntensity = globalSettings["sunIntensity"];
        viewer._renderSettings.ambientIntensity = globalSettings["ambientIntensity"];
        viewer._renderSettings.sunAzimuth = globalSettings["sunAzimuth"];
        viewer._renderSettings.sunElevation = globalSettings["sunElevation"];
        viewer.updateRenderSettings(viewer._renderSettings);
        setUpdate(true);
    };

    const handleSunColorChange = (newValue: any) => {
        //console.log("newValue.hex:", newValue.hex.toUpperCase());
        globalSettings["sunColor"] = newValue.hex.toUpperCase();
        updateRenderSettings();
    };

    const handleAmbientColorChange = (newValue: any) => {
        globalSettings["ambientColor"] = newValue.hex.toUpperCase();
        updateRenderSettings();
    };

    const setSunIntensity = (newValue: any) => {
        globalSettings["sunIntensity"] = newValue;
        updateRenderSettings();
    };

    const setAmbientIntensity = (newValue: any) => {
        globalSettings["ambientIntensity"] = newValue;
        updateRenderSettings();
    };

    const setSunAzimuth = (newValue: any) => {
        globalSettings["sunAzimuth"] = newValue;
        updateRenderSettings();
    };

    const setSunElevation = (newValue: any) => {
        globalSettings["sunElevation"] = newValue;
        updateRenderSettings();
    };

    return (
        <div>
            {props.show ? (
                <div id="lightingPanel" ref={(elm) => (panel = elm)} className={classes.root}>
                    <div>
                        <div className={classes.centerDiv}>
                            <div className={classes.content}>
                                <table className={classes.table}>
                                    <tbody>
                                        <tr>
                                            <td align="left" className={classes.cell}>
                                                Sun Color
                                            </td>
                                            <td align="right" className={classes.cell}>
                                                <div className={classes.inputColor}>
                                                    <input
                                                        value={sunColorValue}
                                                        className={classes.sliderInput}
                                                        onChange={(e) => {
                                                            handleColorChange("sunColor", e.target.value);
                                                        }}
                                                    />
                                                    <div
                                                        className={classes.colorBox}
                                                        style={{ backgroundColor: sunColor }}
                                                        onClick={(e) => showPalette("sunColor")}
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td
                                                ref={(elm) => (sunColorRef = elm)}
                                                colSpan={2}
                                                style={{ display: "none" }}
                                                align="center"
                                            >
                                                <div className={classes.palette}>
                                                    <SketchPicker
                                                        color={sunColor}
                                                        onChangeComplete={handleSunColorChange}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td align="left" className={classes.cell}>
                                                Ambient Color
                                            </td>
                                            <td align="right" className={classes.cell}>
                                                <div className={classes.inputColor}>
                                                    <input
                                                        value={ambientColorValue}
                                                        className={classes.sliderInput}
                                                        onChange={(e) => {
                                                            handleColorChange("ambientColor", e.target.value);
                                                        }}
                                                    />
                                                    <div
                                                        className={classes.colorBox}
                                                        style={{ backgroundColor: ambientColor }}
                                                        onClick={(e) => showPalette("ambientColor")}
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td
                                                ref={(elm) => (ambientColorRef = elm)}
                                                colSpan={2}
                                                style={{ display: "none" }}
                                                align="center"
                                            >
                                                <div className={classes.palette}>
                                                    <SketchPicker
                                                        color={ambientColor}
                                                        onChangeComplete={handleAmbientColorChange}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td align="left" className={classes.cell}>
                                                Sun Intensity
                                            </td>
                                            <td align="right" className={classes.cell}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={1}
                                                    step={0.01}
                                                    value={sunIntensity}
                                                    className={classes.sliderInput}
                                                    onChange={(e) => {
                                                        setSunIntensity(e.target.value);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td align="left" className={classes.cell}>
                                                Ambient Intensity
                                            </td>
                                            <td align="right" className={classes.cell}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={1}
                                                    step={0.01}
                                                    value={ambientIntensity}
                                                    className={classes.sliderInput}
                                                    onChange={(e) => {
                                                        setAmbientIntensity(e.target.value);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td align="left" className={classes.cell}>
                                                Azimuth
                                            </td>
                                            <td align="right" className={classes.cell}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={361}
                                                    step={5}
                                                    value={sunAzimuth}
                                                    className={classes.sliderInput}
                                                    onChange={(e) => {
                                                        setSunAzimuth(parseInt(e.target.value) % 360);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td align="left" className={classes.cell}>
                                                Elevation
                                            </td>
                                            <td align="right" className={classes.cell}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={90}
                                                    step={1}
                                                    value={sunElevation}
                                                    className={classes.sliderInput}
                                                    onChange={(e) => {
                                                        setSunElevation(e.target.value);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                ""
            )}
        </div>
    );
}
